import { useMemo } from "react";
import type { TActivityFe } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/Activity.type";
import type { TActivity2Fe } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/Activity2.type";
import {
    useGetActivityByIdQuery,
    useGetActivityCreateTemplateByIdQuery,
    useGetActivityImportByIdQuery,
} from "@timeedit/schedule-fe-lib/am-be";
import { TActivityRow, isActivity2 } from "../components/activityinfo/ActivityInfo.utils";
import { activity1ToRows } from "../components/activityinfo/Activity1Info.utils";
import { activity2ToRows } from "../components/activityinfo/Activity2Info.utils";

type TActivityRowsResult = {
    activity: TActivityFe | TActivity2Fe | undefined;
    rows: TActivityRow[];
    isLoading: boolean;
    isError: boolean;
};

/**
 * Loads an activity and it's source create template and maps all necessary labels
 */
export const useActivityRows = (
    organizationId: string | undefined,
    activityId: string
): TActivityRowsResult => {
    const activityQuery = useGetActivityByIdQuery(
        { organizationId: organizationId ?? "", activityId },
        { skip: !organizationId }
    );
    const activity = activityQuery.data;
    const sourceId = activity && isActivity2(activity) ? activity.sourceId : undefined;

    const importQuery = useGetActivityImportByIdQuery(
        { organizationId: organizationId ?? "", activityImportId: sourceId ?? "" },
        { skip: !organizationId || !sourceId }
    );
    const activityCreateTemplateId = importQuery.data?.activityCreateTemplateId;

    const templateQuery = useGetActivityCreateTemplateByIdQuery(
        {
            organizationId: organizationId ?? "",
            activityCreateTemplateId: activityCreateTemplateId ?? "",
        },
        { skip: !organizationId || !activityCreateTemplateId }
    );
    const template = templateQuery.data;

    const rows = useMemo(() => {
        if (!activity) {
            return [];
        }
        return isActivity2(activity)
            ? activity2ToRows(activity, template)
            : activity1ToRows(activity);
    }, [activity, template]);

    return {
        activity,
        rows,
        isLoading: activityQuery.isLoading || importQuery.isLoading || templateQuery.isLoading,
        isError: activityQuery.isError,
    };
};
